import {
  Component,
  OnInit,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  AfterViewInit,
  DestroyRef,
  inject,
  signal,
  computed,
  effect,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { SecretsService } from '../../shared/services/secrets/secrets.service';
import { SecretsResourceStore } from '../../shared/services/secrets/secrets-resource.store';
import { DialogService } from '../../shared/services/dialog/dialog.service';
import {
  Secret,
  CreateSecretRequest,
  UpdateSecretRequest,
  SecretSourceType,
  POINTER_SOURCE_TYPES,
} from '../../shared/interfaces/secrets.interface';
import { SecretErrorHandlingUtils } from '../../shared/utils/secret-error-handling.utils';
import { SecretDisplay, buildSecretDisplays } from './secret-display.mapper';

/**
 * A GCP secret value is a pointer: either a short secret id (`A-Za-z0-9_-`) or a
 * full `projects/.../secrets/.../versions/...` resource name.
 */
const GCP_VALUE_PATTERN =
  /^([A-Za-z0-9_-]+|projects\/[^/]+\/secrets\/[^/]+\/versions\/.+)$/;

enum SecretFormMode {
  ADD = 'add',
  EDIT = 'edit',
}

@Component({
  selector: 'app-secrets',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatSortModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
  ],
  templateUrl: './secrets.component.html',
  styleUrls: ['./secrets.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SecretsComponent implements OnInit, AfterViewInit {
  private secretsService = inject(SecretsService);
  private readonly store = inject(SecretsResourceStore);
  private readonly dialogService = inject(DialogService);
  private readonly destroyRef = inject(DestroyRef);
  private fb = inject(FormBuilder);

  @Input() projectId: number | null = null;
  /** Emitted when user needs to create a secret source first (e.g. parent can switch to Secret Sources tab) */
  @Output() needSecretSource = new EventEmitter<void>();

  // Reads are backed by the shared SecretsResourceStore (Phase 0); the table
  // still uses MatTableDataSource so MatSort keeps working, kept in sync with
  // the store-derived rows via an effect.
  dataSource = new MatTableDataSource<SecretDisplay>([]);
  displayedColumns: string[] = ['name', 'sourceName', 'gettable', 'actions'];
  readonly secretSources = this.store.secretSources;
  readonly loading = this.store.isLoading;
  readonly displaySecrets = computed<SecretDisplay[]>(() =>
    buildSecretDisplays(this.store.secrets(), this.store.secretSources()),
  );

  /** Surfaces failures from non-form actions (e.g. delete) at the top of the list. */
  actionError = signal<string | null>(null);

  // Inline form state
  showAddForm = signal(false);
  addForm: FormGroup;
  addFormLoading = signal(false);
  addFormError = signal<string | null>(null);

  // Edit form state
  showEditForm = signal(false);
  editForm: FormGroup;
  editFormLoading = signal(false);
  editFormError = signal<string | null>(null);
  editingSecret = signal<Secret | null>(null);

  // Value visibility states
  hideValue = signal(true);
  hideEditValue = signal(true);

  // Source type of the currently-selected source in each form. Drives the value
  // field's label/help/validation: for pointer sources (Vault/GCP) the value is
  // a path/reference, not the secret itself (SCRUM-783). This does NOT affect
  // `gettable` — that's an admin choice per secret, independent of source type.
  readonly addSourceType = signal<SecretSourceType | null>(null);
  readonly editSourceType = computed<SecretSourceType | null>(
    () => this.editingSecret()?.sourceType ?? null,
  );
  readonly isAddPointerSource = computed(() => this.isPointer(this.addSourceType()));
  readonly isEditPointerSource = computed(() => this.isPointer(this.editSourceType()));

  @ViewChild(MatSort) sort!: MatSort;

  constructor() {
    this.addForm = this.createAddForm();
    this.editForm = this.createEditForm();
    // Keep the table data source in sync with the store-derived display rows.
    effect(() => {
      this.dataSource.data = this.displaySecrets();
    });
  }

  ngOnInit(): void {
    this.store.setProjectId(this.projectId);
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  /**
   * Universal form generator based on mode
   */
  private generateSecretForm(mode: SecretFormMode): FormGroup {
    const baseValidators = {
      name: [Validators.required, Validators.minLength(2)],
      sourceId: [Validators.required],
      gettable: [],
    };

    const formConfig: any = {
      name: ['', baseValidators.name],
      sourceId: [null, baseValidators.sourceId],
      gettable: [true, baseValidators.gettable],
    };

    // Configure based on mode
    switch (mode) {
      case SecretFormMode.ADD:
        formConfig.sourceId = [
          this.getDefaultSourceId(),
          baseValidators.sourceId,
        ];
        formConfig.value = ['', [Validators.required]];
        break;

      case SecretFormMode.EDIT:
        // The source cannot be changed when editing, so disable the control via
        // its config rather than a template [disabled] binding on the reactive
        // control (which triggers NG01051). The update request never reads
        // sourceId, so excluding it from the form value is fine.
        formConfig.sourceId = [
          { value: null, disabled: true },
          baseValidators.sourceId,
        ];
        formConfig.value = ['', []]; // Value not required for edit (can be empty to keep current)
        break;
    }

    return this.fb.group(formConfig);
  }

  /**
   * Universal field validation for both forms
   */
  private validateSecretFormField(
    form: FormGroup,
    fieldName: string,
  ): string | null {
    const field = form.get(fieldName);
    if (field && field.invalid && (field.dirty || field.touched)) {
      if (field.errors?.['required']) {
        return `${this.getFieldLabel(fieldName)} is required`;
      }
      if (field.errors?.['minlength']) {
        return `${this.getFieldLabel(fieldName)} must be at least ${field.errors['minlength'].requiredLength} characters`;
      }
      if (field.errors?.['pattern']) {
        // The only patterned field is the GCP value (short id or resource name).
        return 'Enter a secret id (letters, digits, - and _) or a full projects/PROJECT/secrets/NAME/versions/VERSION resource name';
      }
    }
    return null;
  }

  /** Refresh secrets and secret sources (e.g. after creating a source in another tab) */
  refreshData(): void {
    this.addFormError.set(null);
    this.actionError.set(null);
    this.store.reload();
  }

  private createAddForm(): FormGroup {
    return this.generateSecretForm(SecretFormMode.ADD);
  }

  private createEditForm(): FormGroup {
    return this.generateSecretForm(SecretFormMode.EDIT);
  }

  private getDefaultSourceId(): number | null {
    const defaultSource = this.secretSources().find(
      (s) => s.sourceType === 'Default',
    );
    return defaultSource ? defaultSource.sourceId : null;
  }

  private isPointer(type: SecretSourceType | null): boolean {
    return type != null && POINTER_SOURCE_TYPES.includes(type);
  }

  /** Source type for a source id, from the loaded sources. */
  private sourceTypeForId(sourceId: number | null): SecretSourceType | null {
    if (sourceId == null) return null;
    return (
      this.secretSources().find((s) => s.sourceId === sourceId)?.sourceType ??
      null
    );
  }

  /** Field label for the value input, by source type. */
  valueLabel(type: SecretSourceType | null): string {
    switch (type) {
      case 'Vault':
        return 'Vault Path';
      case 'GcpSecretManager':
        return 'GCP Secret ID or Resource Name';
      default:
        return 'Secret Value';
    }
  }

  /** Placeholder/help for the value input, by source type. */
  valueHint(type: SecretSourceType | null): string {
    switch (type) {
      case 'Vault':
        return 'A path/reference into Vault — not the secret itself.';
      case 'GcpSecretManager':
        return 'A short secret id (letters, digits, - and _) or a full projects/PROJECT/secrets/NAME/versions/VERSION resource name (e.g. versions/latest) — not the secret itself.';
      default:
        return '';
    }
  }

  /**
   * Apply value-field validators for the selected source type. The value is
   * required on add (optional on edit, where blank keeps the current value);
   * GCP additionally enforces the id/resource-name format.
   */
  private applyValueValidators(
    form: FormGroup,
    type: SecretSourceType | null,
    mode: SecretFormMode,
  ): void {
    const value = form.get('value');
    if (!value) return;
    const validators = [];
    if (mode === SecretFormMode.ADD) validators.push(Validators.required);
    if (type === 'GcpSecretManager') validators.push(Validators.pattern(GCP_VALUE_PATTERN));
    value.setValidators(validators);
    value.updateValueAndValidity();
  }

  /**
   * React to a source change in the add form: track the source type and refresh
   * the value validators (label/format depend on whether the value is a
   * pointer). `gettable` is left to the admin and unchanged here.
   */
  onAddSourceChange(): void {
    const type = this.sourceTypeForId(this.addForm.get('sourceId')?.value);
    this.addSourceType.set(type);
    this.applyValueValidators(this.addForm, type, SecretFormMode.ADD);
  }

  onAddNew(): void {
    const validationResult = this.validateAndPrepareAddForm();
    if (!validationResult.success) {
      if (validationResult.error?.includes('No secret sources available')) {
        this.needSecretSource.emit();
        return;
      }
      this.addFormError.set(validationResult.error!);
      this.showAddForm.set(false);
      return;
    }

    this.initializeAddForm(validationResult.sourceId!);
  }

  /**
   * Validate prerequisites and determine source ID for add form
   */
  private validateAndPrepareAddForm(): {
    success: boolean;
    sourceId?: number;
    error?: string;
  } {
    if (!this.secretSources() || this.secretSources().length === 0) {
      return {
        success: false,
        error:
          'No secret sources available. Please create a secret source first.',
      };
    }

    const sourceId = this.resolveSourceId();
    if (!sourceId) {
      return {
        success: false,
        error:
          'Unable to determine default secret source. Please ensure at least one secret source exists.',
      };
    }

    return { success: true, sourceId };
  }

  /**
   * Initialize the add form with default values
   */
  private initializeAddForm(sourceId: number): void {
    this.showAddForm.set(true);
    this.showEditForm.set(false);
    this.addFormError.set(null);
    this.addForm.reset();
    this.addForm.patchValue({
      sourceId: sourceId,
      gettable: true,
    });
    // Initialize source-type-dependent state (value label/validation, gettable).
    this.onAddSourceChange();
  }

  /**
   * Resolve which source ID to use (default or first available)
   */
  private resolveSourceId(): number | null {
    const defaultSourceId = this.getDefaultSourceId();
    return defaultSourceId ?? this.secretSources()[0]?.sourceId ?? null;
  }

  private closeAllForms(): void {
    this.showAddForm.set(false);
    this.showEditForm.set(false);
    this.addForm.reset();
    this.editForm.reset();
    this.addFormError.set(null);
    this.editFormError.set(null);
    this.editingSecret.set(null);
    this.hideValue.set(true);
    this.hideEditValue.set(true);
    this.addSourceType.set(null);
  }

  onCancelAdd(): void {
    this.closeAllForms();
  }

  onSaveAdd(): void {
    if (!this.validateForm(this.addForm)) {
      return;
    }

    this.addFormLoading.set(true);
    this.addFormError.set(null);

    const request = this.createSecretRequestFromForm();
    this.createSecret(request);
  }

  /**
   * Validate a form (generic for both add and edit)
   */
  private validateForm(form: FormGroup): boolean {
    if (form.invalid || !this.projectId) {
      form.markAllAsTouched();
      return false;
    }
    return true;
  }

  /**
   * Create a CreateSecretRequest from form values
   */
  private createSecretRequestFromForm(): CreateSecretRequest {
    const formValue = this.addForm.value;
    return {
      name: formValue.name,
      value: formValue.value,
      sourceId: formValue.sourceId,
      gettable: formValue.gettable,
    };
  }

  /**
   * Create secret via service
   */
  private createSecret(request: CreateSecretRequest): void {
    this.secretsService
      .createSecret(this.projectId!, request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.handleSecretOperationSuccess(true),
        error: (error) => this.handleSecretOperationError(error, true),
      });
  }

  /**
   * Handle successful secret operation (create or update)
   */
  private handleSecretOperationSuccess(isCreate: boolean): void {
    if (isCreate) {
      this.addFormLoading.set(false);
    } else {
      this.editFormLoading.set(false);
    }
    this.closeAllForms();
    this.store.reload();
  }

  /**
   * Handle secret operation error (create or update)
   */
  private handleSecretOperationError(error: any, isCreate: boolean): void {
    const errorMessage = SecretErrorHandlingUtils.getSecretErrorMessage(error);
    if (isCreate) {
      this.addFormLoading.set(false);
      this.addFormError.set(errorMessage);
    } else {
      this.editFormLoading.set(false);
      this.editFormError.set(errorMessage);
    }
  }

  getAddFormFieldError(fieldName: string): string | null {
    return this.validateSecretFormField(this.addForm, fieldName);
  }

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      name: 'Secret Name',
      value: 'Secret Value',
      sourceId: 'Source',
    };
    return labels[fieldName] || fieldName;
  }

  onEdit(secret: Secret): void {
    this.editingSecret.set(secret);
    this.showEditForm.set(true);
    this.showAddForm.set(false); // Close add form if open
    this.editFormError.set(null);

    // Populate the form with existing data
    this.editForm.patchValue({
      name: secret.name,
      value: '', // Don't pre-populate password for security
      sourceId: secret.sourceId,
      gettable: secret.gettable,
    });
    // GCP edits still validate the value format when a new value is entered.
    this.applyValueValidators(this.editForm, secret.sourceType, SecretFormMode.EDIT);
  }

  onCancelEdit(): void {
    this.closeAllForms();
  }

  onSaveEdit(): void {
    if (!this.validateEditForm()) {
      return;
    }

    this.editFormLoading.set(true);
    this.editFormError.set(null);

    const request = this.createUpdateRequestFromForm();
    this.updateSecret(request);
  }

  /**
   * Validate the edit form
   */
  private validateEditForm(): boolean {
    if (!this.editingSecret()) {
      return false;
    }
    return this.validateForm(this.editForm);
  }

  /**
   * Create an UpdateSecretRequest from form values
   */
  private createUpdateRequestFromForm(): UpdateSecretRequest {
    const formValue = this.editForm.value;
    const request: UpdateSecretRequest = {
      name: formValue.name,
      gettable: formValue.gettable,
    };

    // Only include value if it's been provided (not empty)
    if (formValue.value && formValue.value.trim() !== '') {
      request.value = formValue.value.trim();
    }

    return request;
  }

  /**
   * Update secret via service
   */
  private updateSecret(request: UpdateSecretRequest): void {
    this.secretsService
      .updateSecret(this.projectId!, this.editingSecret()!.secretId, request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.handleSecretOperationSuccess(false),
        error: (error) => this.handleSecretOperationError(error, false),
      });
  }

  getEditFormFieldError(fieldName: string): string | null {
    return this.validateSecretFormField(this.editForm, fieldName);
  }

  onDelete(secret: Secret): void {
    if (!this.projectId) return;

    this.actionError.set(null);
    this.secretsService
      .deleteSecret(this.projectId, secret.secretId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.store.reload();
        },
        error: (error) => {
          this.actionError.set(
            SecretErrorHandlingUtils.getSecretErrorMessage(error),
          );
        },
      });
  }

  onViewValue(secret: Secret): void {
    if (!secret.gettable || !this.projectId) {
      return;
    }

    this.dialogService
      .secretValueDialog({
        secret: secret,
        projectId: this.projectId,
      })
      .subscribe((_result) => {
        // Handle any post-dialog actions if needed
      });
  }

  onActionMenu(secret: Secret, action: string): void {
    switch (action) {
      case 'view':
        this.onViewValue(secret);
        break;
      case 'edit':
        this.onEdit(secret);
        break;
      case 'delete':
        this.onDelete(secret);
        break;
    }
  }
}
